// Accounts Exports SERVICE — cloned from src/blank/blank.ts (Rule 0), renamed.
//
// ⚠️ `paramsSerializer` IS LOAD-BEARING on every call that sends `filter`. The
// backend binds `?filter=<json>&filter=<json>` (the repeated BARE key); axios's
// default emits `filter[]=`, which FastAPI binds to nothing — a 200 with the
// WHOLE table. See the header of src/blank/blank.ts before changing anything.
import { AxiosResponse } from "axios";
import { CacheRequestConfig } from "axios-cache-interceptor";
import queryString from "query-string";

import { axios } from "../api";
import type { AeronetV6Response } from "../types";

import type { ExportBatch, ExportBatchLines } from "./types";

/** Paginator ceiling. `size` above this is a 422, not a silent clamp. */
export const MAX_PAGE_SIZE = 100;

/** The History discriminator is the TABLE name (app/history: source_type = __tablename__);
 *  the History tab passes it to the shared RecordHistoryTab. */
export const HISTORY_SOURCE_TYPE = "myob_import";

export const eq = (field: string, value: unknown): string =>
  JSON.stringify({ field, op: "==", value });

const BASE = "/v6/batchexports";

/**
 * LIST — the grid feed. Envelope: Page aliases `items` → `data`, and this route
 * keeps the alias, so it reads `.data.data`.
 */
export async function listBatchExports(
  params?: Record<string, any>,
  axiosConfig?: CacheRequestConfig
): Promise<{ total: number; rows: ExportBatch[] }> {
  const resp: AxiosResponse<AeronetV6Response<ExportBatch>> = await axios.get(BASE, {
    params,
    // ⚠️ LOAD-BEARING — see the header.
    paramsSerializer: (p) => queryString.stringify(p),
    ...axiosConfig,
    id: "get-batchexports-list",
  });
  axios.storage.remove("get-batchexports-list");
  return { total: resp.data.total, rows: [...resp.data.data] };
}

export async function getBatchExport(id: number): Promise<ExportBatch> {
  const resp = await axios.get<ExportBatch>(`${BASE}/${id}`, { cache: false });
  return resp.data;
}

export async function createBatchExport(body: Partial<ExportBatch>): Promise<ExportBatch> {
  const resp = await axios.post<ExportBatch>(BASE, body);
  return resp.data;
}

export async function updateBatchExport(id: number, body: Partial<ExportBatch>): Promise<ExportBatch> {
  const resp = await axios.put<ExportBatch>(`${BASE}/${id}`, body);
  return resp.data;
}

/** DETAILS — every line of the batch in one read; the tab pages and searches them client-side. */
export async function getBatchExportLines(
  id: number,
  includeZero: boolean,
  axiosConfig?: CacheRequestConfig
): Promise<ExportBatchLines> {
  const resp = await axios.get<ExportBatchLines>(`${BASE}/${id}/lines`, {
    params: { include_zero: includeZero },
    cache: false,
    ...axiosConfig,
  });
  return resp.data;
}

export interface ExportBatchRefusal {
  id: number;
  reason: string;
}

export interface ExportBatchFromRecordsResult {
  batch: ExportBatch;
  /** The record ids stamped (for Orders: the ORDER ids whose lines were taken). */
  stamped: number[];
  /** Every ticked record left out, and why. A 201 is not per-record success (BP-20). */
  refused: ExportBatchRefusal[];
}

/** The 422 answer when no ticked record can be exported: nothing was written. */
export class NothingToExportError extends Error {
  constructor(public refused: ExportBatchRefusal[]) {
    super("None of the ticked records can be exported");
  }
}

/**
 * CREATE FROM RECORDS — the source modules' ACTIONS → Create export batch
 * (Aaron 2026-09-30: "pickers are action menus in the module · multi tick and
 * create batch"). The caller reports the outcome, so the global toast is
 * suppressed: a 422's `detail` here is an object (message + per-record
 * reasons) the interceptor would print as "[object Object]".
 */
export async function createBatchExportFromRecords(
  typeId: number,
  ids: number[]
): Promise<ExportBatchFromRecordsResult> {
  try {
    const resp = await axios.post<ExportBatchFromRecordsResult>(
      `${BASE}/from-records`,
      { type_id: typeId, ids },
      { suppressErrorToast: true }
    );
    return resp.data;
  } catch (e: any) {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 422 && Array.isArray(detail?.refused)) {
      throw new NothingToExportError(detail.refused);
    }
    throw e;
  }
}
